'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Phone, 
  PhoneOff, 
  ArrowRight, 
  RotateCcw, 
  AlertTriangle, 
  ShieldCheck, 
  Wifi, 
  Battery, 
  MicOff, 
  Grid, 
  Volume2, 
  Plus, 
  Video, 
  User, 
  Clock, 
  MessageSquare,
  AlertCircle,
  ShieldAlert
} from 'lucide-react';
import { WaveformBars } from './waveform-bars';
import { trackEvent } from '@/lib/analytics';

type SimulatorState = 'idle' | 'ringing' | 'connected' | 'declined' | 'ended';
type PersonaId = 'daughter' | 'son' | 'grandchild';

interface ScriptLine {
  speaker: 'AI Clone' | 'Parent' | 'Trustbox Reveal';
  text: string;
  audioState: 'clone' | 'parent' | 'reveal' | 'silent';
}

const SCRIPT: ScriptLine[] = [
  {
    speaker: 'AI Clone',
    text: "Mom? Please help me, I got into a bad car accident...",
    audioState: 'clone',
  },
  {
    speaker: 'Parent',
    text: "Oh my god! Honey! Are you okay? Where are you? What happened?!",
    audioState: 'parent',
  },
  {
    speaker: 'AI Clone',
    text: "I got arrested because they say I caused the accident. They say I need $2,000 for bail right now or I'll go to jail... please don't tell anyone, I'm so scared...",
    audioState: 'clone',
  },
  {
    speaker: 'Parent',
    text: "I'm sending it right now! Tell me how, do I wire it? Where are you?",
    audioState: 'parent',
  },
  {
    speaker: 'AI Clone',
    text: "They want it in Apple Gift Cards or Venmo... please hurry mom, they're taking my phone...",
    audioState: 'clone',
  },
  {
    speaker: 'Trustbox Reveal',
    text: "This was a test call. The voice sounded exactly like your child, but it was cloned using AI. In a real scam, your family could have lost thousands of dollars in minutes. Prepare your parents before a real call arrives.",
    audioState: 'reveal',
  },
];

const PERSONAS = {
  daughter: {
    id: 'daughter' as PersonaId,
    caller: 'Sarah (Daughter)',
    receiver: 'Mother',
    label: 'Daughter calling Mom',
    callerLabel: 'Daughter (AI Clone)',
    receiverLabel: 'Mother',
    name: 'Sarah',
    initials: 'S',
    gradient: 'linear-gradient(135deg, #EC4899, #F43F5E)',
    avatar: '/images/sarah_avatar.png',
    replaceMap: {} as Record<string, string>
  },
  son: {
    id: 'son' as PersonaId,
    caller: 'Alex (Son)',
    receiver: 'Mother',
    label: 'Son calling Mom',
    callerLabel: 'Son (AI Clone)',
    receiverLabel: 'Mother',
    name: 'Alex',
    initials: 'A',
    gradient: 'linear-gradient(135deg, #3B82F6, #10B981)',
    avatar: '/images/alex_avatar.png',
    replaceMap: {
      "Honey!": "Son!",
      "child": "son"
    }
  },
  grandchild: {
    id: 'grandchild' as PersonaId,
    caller: 'Emily (Grandchild)',
    receiver: 'Grandmother',
    label: 'Grandchild calling Grandma',
    callerLabel: 'Grandchild (AI Clone)',
    receiverLabel: 'Grandmother',
    name: 'Emily',
    initials: 'E',
    gradient: 'linear-gradient(135deg, #8B5CF6, #EC4899)',
    avatar: '/images/emily_avatar.png',
    replaceMap: {
      "Mom?": "Grandma?",
      "mom": "grandma",
      "Honey!": "Honey!",
      "child": "grandchild"
    }
  }
};

function highlightText(text: string) {
  if (!text) return [];
  const keywords = [
    /accident/gi,
    /bail/gi,
    /jail/gi,
    /\$2,000/gi,
    /Apple Gift Cards/gi,
    /Venmo/gi,
    /cloned/gi,
    /AI voice clone/gi,
    /scam/gi,
    /lost/gi,
    /arrested/gi,
    /taking my phone/gi,
    /hurry mom/gi,
    /emergency/gi,
  ];

  let parts: { text: string; highlight: boolean }[] = [{ text, highlight: false }];

  for (const regex of keywords) {
    const newParts: typeof parts = [];
    for (const part of parts) {
      if (part.highlight) {
        newParts.push(part);
        continue;
      }
      const matches = part.text.match(regex);
      if (matches) {
        const splitText = part.text.split(regex);
        for (let i = 0; i < splitText.length; i++) {
          newParts.push({ text: splitText[i], highlight: false });
          if (i < splitText.length - 1) {
            newParts.push({ text: matches[i] || matches[0], highlight: true });
          }
        }
      } else {
        newParts.push(part);
      }
    }
    parts = newParts;
  }

  return parts.map((part, index) => {
    if (part.highlight) {
      return (
        <span
          key={index}
          style={{
            color: '#F87171',
            fontWeight: 800,
            textShadow: '0 0 8px rgba(239, 68, 68, 0.4)',
            background: 'rgba(239, 68, 68, 0.12)',
            padding: '1px 3px',
            borderRadius: '4px',
            border: '1px solid rgba(239, 68, 68, 0.2)',
          }}
        >
          {part.text}
        </span>
      );
    }
    return <span key={index}>{part.text}</span>;
  });
}

export function PhoneSimulator() {
  const [state, setState] = useState<SimulatorState>('idle');
  const [selectedPersona, setSelectedPersona] = useState<PersonaId>('daughter');
  const [activeLineIndex, setActiveLineIndex] = useState<number>(-1);
  const [timerSeconds, setTimerSeconds] = useState<number>(0);
  const [isPlayingAudio, setIsPlayingAudio] = useState<boolean>(false);
  const [typedText, setTypedText] = useState<string>('');
  const [systemTime, setSystemTime] = useState<string>('9:41 AM');
  const [diagnosticLogs, setDiagnosticLogs] = useState<string[]>([]);

  // Dynamic system time updater (iOS clock)
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      let hours = now.getHours();
      const minutes = now.getMinutes().toString().padStart(2, '0');
      const ampm = hours >= 12 ? 'PM' : 'AM';
      hours = hours % 12;
      hours = hours ? hours : 12; // 0 hour becomes 12
      setSystemTime(`${hours}:${minutes} ${ampm}`);
    };
    updateTime();
    const interval = setInterval(updateTime, 60000);
    return () => clearInterval(interval);
  }, []);

  // Cyber security diagnostic logs typist
  useEffect(() => {
    if (state !== 'ended') {
      setDiagnosticLogs([]);
      return;
    }
    const logLines = [
      '[INIT] Loading Voice Audio Scanner...',
      '[SCAN] Analyzing frequency & timbre resonance...',
      '[WARN] Timbre matches AI clone signature (99.8%)',
      '[SCAN] Evaluating response stress context...',
      '[ALERT] High urgency & panic detected',
      '[SCAN] Auditing transaction payload parameters...',
      '[ALERT] Venmo / Apple Gift Card wire approved',
      '[FAIL] Vulnerability Index: 98% (CRITICAL RISK)'
    ];
    let currentIdx = 0;
    // Clear out initially
    setDiagnosticLogs([]);
    const interval = setInterval(() => {
      if (currentIdx < logLines.length) {
        setDiagnosticLogs((prev) => [...prev, logLines[currentIdx]]);
        currentIdx++;
      } else {
        clearInterval(interval);
      }
    }, 450);
    return () => clearInterval(interval);
  }, [state]);
  
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const audioPlayerRef = useRef<HTMLAudioElement | null>(null);
  const ringIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const transcriptBottomRef = useRef<HTMLDivElement | null>(null);
  const transcriptContainerRef = useRef<HTMLDivElement | null>(null);
  const typewriterTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Play browser synthesizer beep for feedback
  const playBeep = (freq = 400, duration = 0.15) => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.frequency.value = freq;
      osc.connect(gain);
      gain.connect(ctx.destination);
      gain.gain.setValueAtTime(0.08, ctx.currentTime);
      osc.start();
      setTimeout(() => {
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.05);
        setTimeout(() => {
          osc.stop();
          ctx.close();
        }, 50);
      }, duration * 1000);
    } catch (err) {
      // audio context blocked
    }
  };

  // Ringtone generation (US style double ring)
  const playRingtoneTick = () => {
    try {
      const AudioCtx = window.AudioContext || (window as any).webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gain = ctx.createGain();
      
      osc1.frequency.value = 440;
      osc2.frequency.value = 480;
      
      osc1.connect(gain);
      osc2.connect(gain);
      gain.connect(ctx.destination);
      
      gain.gain.setValueAtTime(0, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.08, ctx.currentTime + 0.1);
      
      osc1.start();
      osc2.start();
      
      setTimeout(() => {
        gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.1);
        setTimeout(() => {
          osc1.stop();
          osc2.stop();
          ctx.close();
        }, 150);
      }, 1800);
    } catch (err) {
      // context blocked
    }
  };

  // Ring interval ticking
  useEffect(() => {
    if (state === 'ringing') {
      playRingtoneTick();
      ringIntervalRef.current = setInterval(playRingtoneTick, 4000);
    } else {
      if (ringIntervalRef.current) {
        clearInterval(ringIntervalRef.current);
        ringIntervalRef.current = null;
      }
    }
    return () => {
      if (ringIntervalRef.current) {
        clearInterval(ringIntervalRef.current);
      }
    };
  }, [state]);

  // Call timer ticking
  useEffect(() => {
    if (state !== 'connected') {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      return;
    }

    timerIntervalRef.current = setInterval(() => {
      setTimerSeconds((prev) => prev + 1);
    }, 1000);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [state]);

  // Automatic scrolling to bottom of captions inside container
  useEffect(() => {
    if (transcriptContainerRef.current) {
      transcriptContainerRef.current.scrollTop = transcriptContainerRef.current.scrollHeight;
    }
  }, [typedText, activeLineIndex]);

  // Pacing / Typewriter effect for transcript synchronized with current audio
  useEffect(() => {
    if (state !== 'connected' || activeLineIndex < 0 || activeLineIndex >= SCRIPT.length) {
      setTypedText('');
      return;
    }

    if (typewriterTimeoutRef.current) {
      clearTimeout(typewriterTimeoutRef.current);
    }

    const fullLineText = getLineText(SCRIPT[activeLineIndex], selectedPersona);
    setTypedText('');
    
    let charIndex = 0;
    const totalChars = fullLineText.length;
    // Estimate typing speed to roughly map to standard audio durations (e.g. 60-70ms per character)
    const speed = Math.max(25, Math.min(65, 3000 / totalChars));

    function typeNextChar() {
      if (charIndex < totalChars) {
        setTypedText(fullLineText.substring(0, charIndex + 1));
        charIndex++;
        typewriterTimeoutRef.current = setTimeout(typeNextChar, speed);
      }
    }

    typeNextChar();

    return () => {
      if (typewriterTimeoutRef.current) {
        clearTimeout(typewriterTimeoutRef.current);
      }
    };
  }, [state, activeLineIndex, selectedPersona]);

  // Audio trigger
  useEffect(() => {
    if (state !== 'connected' || activeLineIndex < 0 || activeLineIndex >= SCRIPT.length) {
      setIsPlayingAudio(false);
      return;
    }

    if (audioPlayerRef.current) {
      audioPlayerRef.current.src = `/audio/${selectedPersona}-line-${activeLineIndex + 1}.mp3`;
      audioPlayerRef.current.load();
      setIsPlayingAudio(true);
      
      audioPlayerRef.current.play().catch((err) => {
        console.warn("Autoplay block (continuing with text simulator):", err);
        // Fallback: If browser blocks audio, proceed programmatically based on text duration
        setIsPlayingAudio(true);
        const textLen = SCRIPT[activeLineIndex].text.length;
        const fakeDuration = Math.max(3500, textLen * 55);
        setTimeout(() => {
          if (state === 'connected') {
            handleAudioEnded();
          }
        }, fakeDuration);
      });
    }
  }, [state, activeLineIndex]);

  const handleAudioEnded = () => {
    setIsPlayingAudio(false);
    if (activeLineIndex < SCRIPT.length - 1) {
      // Pause between dialog blocks for realistic pauses
      setTimeout(() => {
        if (state === 'connected') {
          setActiveLineIndex((prev) => prev + 1);
        }
      }, 900);
    } else {
      playBeep(300, 0.4);
      setState('ended');
      trackEvent('phone_simulator_completed', { persona: selectedPersona });
    }
  };

  const handleStart = () => {
    playBeep(600, 0.1);
    setState('ringing');
    setTimerSeconds(0);
    setActiveLineIndex(-1);
    trackEvent('phone_simulator_started', { persona: selectedPersona });
  };

  const handleAnswer = () => {
    playBeep(800, 0.1);
    setState('connected');
    setTimerSeconds(0);
    setActiveLineIndex(0);
  };

  const handleDecline = () => {
    playBeep(300, 0.3);
    setState('declined');
    trackEvent('phone_simulator_declined', { persona: selectedPersona });
  };

  const handleReset = () => {
    playBeep(500, 0.15);
    setState('idle');
    setTimerSeconds(0);
    setActiveLineIndex(-1);
    setIsPlayingAudio(false);
    setTypedText('');
    if (audioPlayerRef.current) {
      audioPlayerRef.current.pause();
    }
  };

  const formatTime = (secs: number) => {
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = (secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const getLineSpeaker = (line: ScriptLine, persona: PersonaId) => {
    const p = PERSONAS[persona];
    if (line.speaker === 'AI Clone') return p.callerLabel;
    if (line.speaker === 'Parent') return p.receiverLabel;
    return 'Trustbox Security Reveal';
  };

  const getLineText = (line: ScriptLine, persona: PersonaId) => {
    let text = line.text;
    const p = PERSONAS[persona];
    Object.entries(p.replaceMap).forEach(([target, replacement]) => {
      text = text.replaceAll(target, replacement);
    });
    return text;
  };

  const pObj = PERSONAS[selectedPersona];
  const currentLine = activeLineIndex >= 0 ? SCRIPT[activeLineIndex] : null;

  return (
    <div style={{ position: 'relative', width: 390, maxWidth: '100%', margin: '0 auto', userSelect: 'none' }}>
      {/* Physical Left Side Buttons (Volume Up, Volume Down, Ring/Silent Switch) */}
      <div style={{ position: 'absolute', left: -14, top: 120, width: 4, height: 32, background: '#334155', borderRadius: '4px 0 0 4px', boxShadow: '-2px 0 4px rgba(0,0,0,0.3)', borderRight: '1px solid rgba(0,0,0,0.4)', zIndex: 10 }} />
      <div style={{ position: 'absolute', left: -14, top: 170, width: 4, height: 60, background: '#334155', borderRadius: '4px 0 0 4px', boxShadow: '-2px 0 4px rgba(0,0,0,0.3)', borderRight: '1px solid rgba(0,0,0,0.4)', zIndex: 10 }} />
      <div style={{ position: 'absolute', left: -14, top: 245, width: 4, height: 60, background: '#334155', borderRadius: '4px 0 0 4px', boxShadow: '-2px 0 4px rgba(0,0,0,0.3)', borderRight: '1px solid rgba(0,0,0,0.4)', zIndex: 10 }} />
      
      {/* Physical Right Side Button (Power Button) */}
      <div style={{ position: 'absolute', right: -14, top: 200, width: 4, height: 80, background: '#334155', borderRadius: '0 4px 4px 0', boxShadow: '2px 0 4px rgba(0,0,0,0.3)', borderLeft: '1px solid rgba(0,0,0,0.4)', zIndex: 10 }} />

      {/* Screen Reflection overlay */}
      <div
        style={{
          position: 'absolute',
          top: 12,
          left: 12,
          right: 12,
          height: 716, // Height minus bezel borders (740 - 24)
          borderRadius: 28,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0) 45%, rgba(255,255,255,0) 100%)',
          pointerEvents: 'none',
          zIndex: 35,
        }}
      />
      
      {/* 3D Phone Body */}
      <motion.div
        animate={state === 'ringing' ? {
          x: [0, -1, 1, -1, 1, 0],
          y: [0, 0.5, -0.5, 0.5, -0.5, 0],
          rotate: [0, -0.15, 0.15, -0.15, 0.15, 0],
        } : {}}
        transition={state === 'ringing' ? {
          repeat: Infinity,
          duration: 0.12,
          ease: "linear"
        } : {}}
        style={{
          maxWidth: 390,
          width: '100%',
          height: 740,
          borderRadius: 40,
          background: '#040B07',
          border: '12px solid #1E293B', // Thicker premium outer bezel
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.6), inset 0 2px 10px rgba(255, 255, 255, 0.05)',
          overflow: 'hidden',
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          outline: '1.5px solid #334155', // Outermost shadow border ring
        }}
      >
      {/* Invisible HTML5 Audio Tag */}
      <audio
        ref={audioPlayerRef}
        onEnded={handleAudioEnded}
        preload="auto"
      />

      {/* Modern Notch Status Bar */}
      <div
        style={{
          height: 38,
          background: '#040B07',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 24px',
          zIndex: 40,
          position: 'relative',
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: '0.6875rem', color: 'white', fontWeight: 600, fontFamily: 'sans-serif', letterSpacing: '-0.1px' }}>{systemTime}</span>
        
        {/* Apple Dynamic Island Notch with speaker slot and camera lens */}
        <div 
          style={{ 
            width: 110, 
            height: 26, 
            background: '#000000', 
            borderRadius: 15, 
            position: 'absolute', 
            left: '50%', 
            transform: 'translateX(-50%)', 
            top: 5,
            border: '1.5px solid rgba(255,255,255,0.06)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 12,
            boxShadow: 'inset 0 1px 4px rgba(255,255,255,0.1)'
          }} 
        >
          {/* Subtle camera lens dot */}
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#111827', border: '1px solid #1e293b' }} />
          {/* Subtle sensor dot */}
          <div style={{ width: 4, height: 4, borderRadius: '50%', background: '#090d16' }} />
        </div>
        
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: 'white' }}>
          <span style={{ fontSize: '0.625rem', color: 'rgba(255,255,255,0.6)', fontWeight: 600, marginRight: 2 }}>5G</span>
          <Wifi size={12} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <span style={{ fontSize: '0.625rem', color: 'rgba(255,255,255,0.7)', fontWeight: 600 }}>88%</span>
            <Battery size={14} />
          </div>
        </div>
      </div>

      <div style={{ flex: 1, padding: '16px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between', position: 'relative', overflow: 'hidden' }}>
        {/* Immersive iOS-style blurred background */}
        {(state === 'ringing' || state === 'connected') && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${pObj.avatar})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              filter: 'blur(32px) brightness(0.35)',
              opacity: 0.9,
              zIndex: 1,
              pointerEvents: 'none',
            }}
          />
        )}
        <AnimatePresence mode="wait">
          
          {/* 1. IDLE STATE (SCENARIO SELECTION & GET STARTED) */}
          {state === 'idle' && (
            <motion.div
              key="idle"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', padding: '8px 12px', position: 'relative', zIndex: 2 }}
            >
              <div style={{ textAlign: 'center', marginTop: 20 }}>
                <div
                  style={{
                    width: 68,
                    height: 68,
                    borderRadius: '50%',
                    background: 'radial-gradient(circle, rgba(34,197,94,0.15) 0%, transparent 80%)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    color: '#4ADE80',
                    boxShadow: '0 0 20px rgba(34,197,94,0.1)',
                  }}
                >
                  <Phone size={30} className="animate-pulse" />
                </div>
                <h4
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: '1.3125rem',
                    fontWeight: 700,
                    color: 'white',
                    marginBottom: 8,
                    lineHeight: 1.3,
                  }}
                >
                  Voice Clone Call Simulation
                </h4>
                <p style={{ fontSize: '0.8125rem', color: 'rgba(255, 255, 255, 0.55)', lineHeight: 1.5, margin: '0 auto 24px', maxWidth: 280 }}>
                  Test how an AI voice clone call sounds on a phone speaker. Choose a target relationship template below:
                </p>

                {/* Persona selector tabs */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, textAlign: 'left', marginBottom: 24 }}>
                  {Object.values(PERSONAS).map((p) => {
                    const active = selectedPersona === p.id;
                    return (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => { playBeep(600, 0.05); setSelectedPersona(p.id); }}
                        style={{
                          padding: '12px 16px',
                          borderRadius: 14,
                          background: active ? 'rgba(34, 197, 94, 0.08)' : 'rgba(255,255,255,0.02)',
                          border: `1px solid ${active ? 'rgba(34, 197, 94, 0.35)' : 'rgba(255,255,255,0.06)'}`,
                          color: active ? '#4ADE80' : 'rgba(255,255,255,0.65)',
                          cursor: 'pointer',
                          fontSize: '0.8125rem',
                          fontWeight: active ? 700 : 500,
                          textAlign: 'left',
                          transition: 'all 0.2s',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          boxShadow: active ? '0 4px 12px rgba(34, 197, 94, 0.05)' : 'none',
                        }}
                      >
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ display: 'block', width: 6, height: 6, borderRadius: '50%', background: active ? '#22C55E' : 'rgba(255,255,255,0.2)' }} />
                          {p.label}
                        </span>
                        <span style={{ fontSize: '0.6875rem', opacity: active ? 1 : 0.4 }}>
                          {active ? 'Selected' : 'Use this'}
                        </span>
                      </button>
                    );
                  })}
                </div>
                
                <p style={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.3)', margin: '12px 0' }}>
                  🔊 Demo uses Sarah&apos;s voice as the clone template
                </p>
              </div>

              <div style={{ marginBottom: 12 }}>
                <button
                  type="button"
                  onClick={handleStart}
                  style={{
                    width: '100%',
                    background: '#22C55E',
                    color: '#052E16',
                    fontWeight: 700,
                    fontSize: '0.9375rem',
                    padding: '16px 24px',
                    borderRadius: 9999,
                    border: 'none',
                    cursor: 'pointer',
                    boxShadow: '0 4px 20px rgba(34, 197, 94, 0.25)',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 8,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 6px 24px rgba(34, 197, 94, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = '';
                    e.currentTarget.style.boxShadow = '0 4px 20px rgba(34, 197, 94, 0.25)';
                  }}
                >
                  Start Demo Simulation
                  <ArrowRight size={16} />
                </button>
              </div>
            </motion.div>
          )}

          {/* 2. RINGING STATE */}
          {state === 'ringing' && (
            <motion.div
              key="ringing"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', height: '100%', textAlign: 'center', padding: '30px 12px', position: 'relative', zIndex: 2 }}
            >
              <div>
                <h4 style={{ fontSize: '1.75rem', fontWeight: 700, color: 'white', marginBottom: 4, fontFamily: 'var(--font-heading)', letterSpacing: '-0.5px' }}>
                  {pObj.caller}
                </h4>
                <p style={{ fontSize: '0.8125rem', color: '#4ADE80', letterSpacing: 0.5, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(34, 197, 94, 0.1)', padding: '4px 12px', borderRadius: 99 }}>
                  Incoming Call...
                </p>
              </div>

              {/* Pulsing profile silhouette / custom avatar with concentric ripples */}
              <div style={{ position: 'relative', width: 150, height: 150, margin: '0 auto', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {/* 3 concentric sound waves with staggered animations */}
                {[0, 1, 2].map((i) => (
                  <motion.div
                    key={i}
                    initial={{ scale: 0.8, opacity: 0.5 }}
                    animate={{ scale: [1, 1.6, 1.8], opacity: [0.5, 0.15, 0] }}
                    transition={{
                      repeat: Infinity,
                      duration: 2.4,
                      delay: i * 0.8,
                      ease: 'easeOut',
                    }}
                    style={{
                      position: 'absolute',
                      width: 90,
                      height: 90,
                      borderRadius: '50%',
                      border: '1.5px solid rgba(239, 68, 68, 0.4)',
                      boxShadow: '0 0 16px rgba(239, 68, 68, 0.15)',
                      pointerEvents: 'none',
                    }}
                  />
                ))}
                
                {/* Core Persona Avatar Card */}
                <div
                  style={{
                    width: 96,
                    height: 96,
                    borderRadius: '50%',
                    backgroundImage: `url(${pObj.avatar})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    border: '3px solid rgba(255, 255, 255, 0.95)',
                    position: 'relative',
                    zIndex: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.6)',
                  }}
                />
              </div>

              {/* Action utilities above buttons */}
              <div style={{ display: 'flex', justifyContent: 'space-around', margin: '0 auto 20px', width: '80%', opacity: 0.4 }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <Clock size={16} style={{ color: 'white' }} />
                  <span style={{ fontSize: '0.625rem', color: 'white' }}>Remind Me</span>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                  <MessageSquare size={16} style={{ color: 'white' }} />
                  <span style={{ fontSize: '0.625rem', color: 'white' }}>Message</span>
                </div>
              </div>

              <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', padding: '0 24px' }}>
                {/* Decline Button */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flex: 1 }}>
                  <button
                    type="button"
                    onClick={handleDecline}
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: '#EF4444',
                      border: 'none',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 4px 16px rgba(239, 68, 68, 0.4)',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = ''; }}
                  >
                    <PhoneOff size={22} />
                  </button>
                  <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>Decline</span>
                </div>

                {/* Answer Button */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, flex: 1 }}>
                  <button
                    type="button"
                    onClick={handleAnswer}
                    style={{
                      width: 60,
                      height: 60,
                      borderRadius: '50%',
                      background: '#22C55E',
                      border: 'none',
                      color: 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 4px 16px rgba(34, 197, 94, 0.4)',
                      position: 'relative',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.08)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = ''; }}
                  >
                    {/* Ringing green pulse */}
                    <span 
                      className="absolute inset-0 rounded-full bg-green-500 animate-ping opacity-25" 
                      style={{ animationDuration: '1.2s' }}
                    />
                    <Phone size={22} />
                  </button>
                  <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>Answer</span>
                </div>
              </div>
            </motion.div>
          )}

          {/* 3. CONNECTED (ACTIVE INCOMING SIMULATED CALL WITH LIVE TRANSCRIBER DRAWER) */}
          {state === 'connected' && (
            <motion.div
              key="connected"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', position: 'relative', zIndex: 2 }}
            >
              {/* Call Info Header */}
              <div style={{ textAlign: 'center', padding: '10px 0' }}>
                <h4 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'white', margin: 0 }}>
                  {pObj.caller}
                </h4>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 4 }}>
                  <span style={{ display: 'block', width: 6, height: 6, borderRadius: '50%', background: '#22C55E' }} />
                  <span style={{ fontSize: '0.75rem', color: '#4ADE80', fontWeight: 600, letterSpacing: 0.5 }}>
                    SIMULATION CONNECTED
                  </span>
                </div>
                <span style={{ fontSize: '0.875rem', color: 'rgba(255,255,255,0.4)', fontFamily: 'monospace', display: 'block', marginTop: 4 }}>
                  {formatTime(timerSeconds)}
                </span>
              </div>

              {/* Sleek iOS calling option buttons grid (3x2) */}
              <div 
                style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(3, 1fr)', 
                  gap: '16px 10px', 
                  maxWidth: '85%', 
                  margin: '10px auto 20px',
                  opacity: 0.85
                }}
              >
                {[
                  { icon: MicOff, label: 'mute', active: false },
                  { icon: Grid, label: 'keypad', active: false },
                  { icon: Volume2, label: 'speaker', active: true },
                  { icon: Plus, label: 'add call', active: false },
                  { icon: Video, label: 'FaceTime', active: false },
                  { icon: User, label: 'contacts', active: false },
                ].map((btn, i) => (
                  <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
                    <div
                      style={{
                        width: 52,
                        height: 52,
                        borderRadius: '50%',
                        background: btn.active ? 'white' : 'rgba(255, 255, 255, 0.08)',
                        border: btn.active ? '1px solid white' : '1px solid rgba(255,255,255,0.05)',
                        color: btn.active ? 'black' : 'white',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        transition: 'all 0.2s',
                        boxShadow: btn.active ? '0 0 16px rgba(255,255,255,0.2)' : 'none',
                      }}
                    >
                      <btn.icon size={20} />
                    </div>
                    <span style={{ fontSize: '0.625rem', color: 'rgba(255, 255, 255, 0.5)', textTransform: 'capitalize' }}>
                      {btn.label}
                    </span>
                  </div>
                ))}
              </div>

              {/* Waveform indicator */}
              {currentLine && (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)', padding: '6px 14px', borderRadius: 99, width: 'fit-content', margin: '0 auto 12px' }}>
                  <span style={{ fontSize: '0.625rem', color: 'rgba(255,255,255,0.4)', fontWeight: 600, textTransform: 'uppercase' }}>
                    {currentLine.audioState === 'clone' ? 'AI Clone Audio' : currentLine.audioState === 'parent' ? 'Parent Audio' : 'Reveal Narrator'}
                  </span>
                  <WaveformBars
                    bars={6}
                    height={12}
                    color={currentLine.audioState === 'clone' ? '#06B6D4' : currentLine.audioState === 'parent' ? '#F59E0B' : '#22C55E'}
                    active={isPlayingAudio}
                  />
                </div>
              )}

              {/* iOS Live Voicemail Captions Panel with high-fidelity glassmorphism */}
              <div
                style={{
                  height: 250,
                  background: 'rgba(5, 18, 12, 0.85)',
                  backdropFilter: 'blur(24px) saturate(190%)',
                  WebkitBackdropFilter: 'blur(24px) saturate(190%)',
                  borderTop: '1.5px solid rgba(34, 197, 94, 0.25)',
                  margin: '0 -16px -16px -16px',
                  padding: '18px 16px 16px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 10,
                  position: 'relative',
                  zIndex: 20,
                  boxShadow: '0 -12px 36px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.05)',
                }}
              >
                {/* Pull handle bar representing drawer */}
                <div style={{ width: 36, height: 5, borderRadius: 3, background: 'rgba(255,255,255,0.25)', position: 'absolute', top: 6, left: '50%', transform: 'translateX(-50%)' }} />

                {/* Live Caption Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.08)', paddingBottom: 10, marginBottom: 4, marginTop: 4 }}>
                  <span style={{ fontSize: '0.6875rem', fontWeight: 800, color: '#fca5a5', letterSpacing: '0.8px', textTransform: 'uppercase', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ display: 'block', width: 8, height: 8, borderRadius: '50%', background: '#EF4444', boxShadow: '0 0 8px #EF4444' }} className="animate-pulse" />
                    Live Voicemail
                  </span>
                  <span style={{ fontSize: '0.625rem', color: 'rgba(255,255,255,0.4)', fontWeight: 600, letterSpacing: '0.3px' }}>
                    Real-time Threat Analysis
                  </span>
                </div>

                {/* Caption dialogue box */}
                <div 
                  ref={transcriptContainerRef}
                  style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}
                >
                  {/* Previous lines */}
                  {activeLineIndex > 0 && SCRIPT.slice(0, activeLineIndex).map((line, idx) => (
                    <div key={idx} style={{ padding: '4px 6px', borderLeft: `2px solid ${line.speaker === 'AI Clone' ? '#06B6D4' : line.speaker === 'Parent' ? '#F59E0B' : '#22C55E'}`, opacity: 0.4 }}>
                      <span style={{ fontSize: '0.625rem', fontWeight: 800, color: line.speaker === 'AI Clone' ? '#06B6D4' : line.speaker === 'Parent' ? '#F59E0B' : '#22C55E', display: 'block', textTransform: 'uppercase' }}>
                        {getLineSpeaker(line, selectedPersona)}
                      </span>
                      <p style={{ fontSize: '0.75rem', color: 'white', margin: '2px 0 0 0', lineHeight: 1.4 }}>
                        {highlightText(getLineText(line, selectedPersona))}
                      </p>
                    </div>
                  ))}

                  {/* Active typewriter line */}
                  {currentLine && (
                    <div style={{ padding: '4px 6px', borderLeft: `3px solid ${currentLine.speaker === 'AI Clone' ? '#06B6D4' : currentLine.speaker === 'Parent' ? '#F59E0B' : '#22C55E'}` }}>
                      <span style={{ fontSize: '0.6875rem', fontWeight: 800, color: currentLine.speaker === 'AI Clone' ? '#06B6D4' : currentLine.speaker === 'Parent' ? '#F59E0B' : '#22C55E', display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                        {getLineSpeaker(currentLine, selectedPersona)}
                      </span>
                      <p style={{ fontSize: '0.8125rem', color: 'white', margin: '4px 0 0 0', lineHeight: 1.45, fontWeight: 500 }}>
                        {highlightText(typedText)}
                        <span style={{ background: '#22C55E', width: 2, height: 13, display: 'inline-block', marginLeft: 2 }} className="animate-pulse" />
                      </p>
                    </div>
                  )}

                  <div ref={transcriptBottomRef} />
                </div>

                {/* Call End Button overlay */}
                <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 8 }}>
                  <button
                    type="button"
                    onClick={() => {
                      playBeep(300, 0.3);
                      setState('ended');
                      trackEvent('phone_simulator_completed', { persona: selectedPersona });
                      if (audioPlayerRef.current) audioPlayerRef.current.pause();
                    }}
                    style={{
                      background: '#EF4444',
                      color: 'white',
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      border: 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 4px 14px rgba(239, 68, 68, 0.4)',
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = ''; }}
                  >
                    <PhoneOff size={20} />
                  </button>
                </div>
              </div>
            </motion.div>
          )}

          {/* 4. DECLINED STATE */}
          {state === 'declined' && (
            <motion.div
              key="declined"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%', textAlign: 'center', padding: '0 12px', position: 'relative', zIndex: 2 }}
            >
              <div
                style={{
                  width: 58,
                  height: 58,
                  borderRadius: '50%',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px',
                  color: '#F87171',
                }}
              >
                <PhoneOff size={26} />
              </div>
              <h4 style={{ fontSize: '1.125rem', fontWeight: 700, color: 'white', marginBottom: 12, fontFamily: 'var(--font-heading)' }}>
                Simulated Call Declined
              </h4>
              
              <div
                style={{
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid rgba(255, 255, 255, 0.06)',
                  borderRadius: 16,
                  padding: 16,
                  marginBottom: 24,
                  textAlign: 'left',
                }}
              >
                <p style={{ color: '#F87171', fontSize: '0.75rem', fontWeight: 700, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <AlertTriangle size={14} /> Correct Instinct, But...
                </p>
                <p style={{ color: '#E7E5E4', fontSize: '0.75rem', lineHeight: 1.45, margin: 0 }}>
                  Declining unknown numbers is correct. However, real criminals spoof local caller IDs (often showing your name) and play a voice that sounds exactly like you.
                </p>
                <p style={{ color: '#A8A29E', fontSize: '0.75rem', lineHeight: 1.45, marginTop: 8, margin: '8px 0 0 0' }}>
                  Under extreme stress (such as a 2 AM emergency call), would your parents hesitate? A simulation prepares them.
                </p>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button
                  type="button"
                  onClick={handleReset}
                  style={{
                    background: '#22C55E',
                    color: '#052E16',
                    fontWeight: 700,
                    padding: '14px 20px',
                    borderRadius: 9999,
                    border: 'none',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    fontSize: '0.875rem',
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 16px rgba(34, 197, 94, 0.2)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = ''; }}
                >
                  Test Call Simulation Anyway
                  <ArrowRight size={14} />
                </button>
                <Link
                  href="/dashboard/simulate"
                  onClick={() =>
                    trackEvent('landing_cta_clicked', {
                      ctaLabel: 'simulator_declined_test',
                      destination: '/dashboard/simulate',
                    })
                  }
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontWeight: 600,
                    padding: '12px 20px',
                    borderRadius: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    fontSize: '0.8125rem',
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                >
                  Configure Safe Call ($4.90)
                </Link>
              </div>
            </motion.div>
          )}

          {/* 5. SEC ALERT STATE (REVEAL & CONCLUDED REVIEW) */}
          {state === 'ended' && (
            <motion.div
              key="ended"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              style={{ display: 'flex', flexDirection: 'column', height: '100%', justifyContent: 'space-between', padding: '4px 2px', position: 'relative', zIndex: 2 }}
            >
              {/* Scan styles */}
              <style dangerouslySetInnerHTML={{__html: `
                @keyframes scanner-sweep {
                  0% { transform: translateY(0); opacity: 0.1; }
                  50% { opacity: 0.8; }
                  100% { transform: translateY(140px); opacity: 0.1; }
                }
                @keyframes neon-alert-pulse {
                  0%, 100% { box-shadow: 0 0 4px rgba(239, 68, 68, 0.2), inset 0 0 4px rgba(239, 68, 68, 0.1); }
                  50% { box-shadow: 0 0 16px rgba(239, 68, 68, 0.5), inset 0 0 8px rgba(239, 68, 68, 0.25); }
                }
              `}} />

              <div style={{ textAlign: 'center' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 12px', borderRadius: 99, background: 'rgba(239, 68, 68, 0.12)', border: '1px solid rgba(239, 68, 68, 0.3)', marginBottom: 10 }}>
                  <ShieldAlert size={13} className="animate-pulse" style={{ color: '#EF4444' }} />
                  <span style={{ fontSize: '0.625rem', fontWeight: 800, color: '#EF4444', letterSpacing: 1.2, textTransform: 'uppercase' }}>
                    Threat Detected
                  </span>
                </div>
                
                <h4 style={{ fontSize: '1rem', fontWeight: 700, color: 'white', marginBottom: 10, fontFamily: 'var(--font-heading)' }}>
                  Vulnerability Scan Report
                </h4>
                
                {/* Vulnerability metrics layout with glowing active scan bar */}
                <div
                  style={{
                    background: 'rgba(10, 15, 12, 0.75)',
                    border: '1px solid #EF4444',
                    borderRadius: 18,
                    padding: '14px 16px',
                    textAlign: 'left',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    marginBottom: 14,
                    position: 'relative',
                    overflow: 'hidden',
                    animation: 'neon-alert-pulse 2.5s infinite ease-in-out',
                  }}
                >
                  {/* Scanner Sweep Line */}
                  <div style={{
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    top: 0,
                    height: 2,
                    background: 'linear-gradient(90deg, transparent, #EF4444, transparent)',
                    boxShadow: '0 0 8px #EF4444',
                    animation: 'scanner-sweep 2s linear infinite',
                    pointerEvents: 'none',
                  }} />

                  <div style={{ borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '0.625rem', color: 'rgba(255,255,255,0.4)', fontWeight: 700, letterSpacing: 0.5 }}>AUDIT STATUS</span>
                      <span style={{ fontSize: '0.625rem', color: '#EF4444', fontWeight: 900, letterSpacing: 0.5 }}>CRITICAL EXPOSURE</span>
                    </div>
                    
                    {/* Simulated Threat Scan Bar */}
                    <div style={{ height: 4, width: '100%', background: 'rgba(255,255,255,0.08)', borderRadius: 99, marginTop: 10, overflow: 'hidden' }}>
                      <motion.div
                        initial={{ width: '0%' }}
                        animate={{ width: '98%' }}
                        transition={{ duration: 1.8, ease: 'easeOut' }}
                        style={{ height: '100%', background: 'linear-gradient(90deg, #EF4444, #F59E0B)', borderRadius: 99 }}
                      />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                      <span style={{ fontSize: '0.625rem', color: 'rgba(255,255,255,0.4)', fontWeight: 700, letterSpacing: 0.5 }}>VULNERABILITY INDEX</span>
                      <span style={{ fontSize: '0.6875rem', color: '#F87171', fontWeight: 800 }}>98% Critical Risk</span>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                    {[
                      { item: 'Voice Clone Recognition', status: '0% (Fooled parent)', pass: false },
                      { item: 'Crisis Bypass Immunity', status: 'FAILED (Panicked response)', pass: false },
                      { item: 'Payment Protocol Shield', status: 'FAILED (Agreed to wire)', pass: false },
                    ].map((m, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.65625rem' }}>
                        <span style={{ color: 'rgba(255,255,255,0.55)' }}>{m.item}</span>
                        <span style={{ color: '#FCD34D', fontWeight: 700 }}>{m.status}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Educational callout */}
                <div
                  style={{
                    background: 'rgba(245, 158, 11, 0.05)',
                    border: '1px solid rgba(245, 158, 11, 0.2)',
                    borderRadius: 14,
                    padding: 10,
                    textAlign: 'left',
                  }}
                >
                  <p style={{ color: '#FBBF24', fontSize: '0.6875rem', fontWeight: 800, margin: '0 0 3px 0', display: 'flex', alignItems: 'center', gap: 5, letterSpacing: 0.2 }}>
                    <ShieldCheck size={13} style={{ color: '#FBBF24' }} /> Action Step: Establish Safe-Word
                  </p>
                  <p style={{ color: '#C4C0BB', fontSize: '0.65625rem', lineHeight: 1.4, margin: 0 }}>
                    In a crisis, panic disables logical reasoning. Establishing a secret family safe-word provides 100% confirmation.
                  </p>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, paddingBottom: 4 }}>
                <Link
                  href="/dashboard/simulate"
                  onClick={() =>
                    trackEvent('landing_cta_clicked', {
                      ctaLabel: 'simulator_ended_test',
                      destination: '/dashboard/simulate',
                    })
                  }
                  style={{
                    background: 'linear-gradient(135deg, #22C55E, #16A34A)',
                    color: '#022C11',
                    fontWeight: 800,
                    padding: '13px 20px',
                    borderRadius: 9999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    fontSize: '0.84375rem',
                    textDecoration: 'none',
                    textAlign: 'center',
                    transition: 'all 0.2s',
                    boxShadow: '0 4px 16px rgba(34, 197, 94, 0.3)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px) scale(1.02)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(34, 197, 94, 0.45)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = '0 4px 16px rgba(34, 197, 94, 0.3)'; }}
                >
                  Setup Safe-Word Shield ($4.90)
                  <ArrowRight size={14} />
                </Link>
                <button
                  type="button"
                  onClick={handleReset}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    color: 'rgba(255,255,255,0.8)',
                    fontWeight: 600,
                    padding: '10px 20px',
                    borderRadius: 9999,
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 6,
                    fontSize: '0.78125rem',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.04)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.3)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.15)'; }}
                >
                  <RotateCcw size={12} />
                  Replay Call Simulation
                </button>
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </div>
      </motion.div>
    </div>
  );
}
